import { isSingleValueData, isTimeSeriesData, Labels } from '@/core';
import { QueryData } from '../runtime';

/**
 * Represents an item (series or value) that may have labels for common label tracking.
 */
export interface LabeledItem {
  /** The raw name of the item, typically containing label information. */
  name: string;

  /**
   * Optional formatted display name for the item.
   * When present, indicates the item uses custom formatting and should stop common label tracking.
   */
  formattedName?: string;

  /** Label key-value pairs associated with this item. */
  labels?: Labels;
}

/**
 * State for tracking common labels across multiple items.
 * This state is mutated in-place to optimize performance (there can be 10k+ series, many labels).
 */
export interface CommonLabelsTrackingState {
  /**
   * Map of label keys to their common values across all items.
   * - Initialized as `undefined` to indicate no items have been processed yet.
   * - Set to a Map when the first item with labels is encountered.
   * - Set back to `undefined` when tracking stops due to mixed data sources.
   * - Labels are removed from the map as items with different values are encountered.
   */
  commonLabelsMap: Map<string, string> | undefined;

  /**
   * Flag to short-circuit processing once common labels become impossible.
   * - Set to `true` when the map becomes empty (no common labels remain).
   * - Set to `true` when items have different formatting strategies (mixed data sources).
   * - Once set to `true`, all subsequent items are ignored for performance.
   */
  stopTracking: boolean;
}

/**
 * Creates the initial state for common labels tracking.
 */
export function createCommonLabelsState(): CommonLabelsTrackingState {
  return {
    commonLabelsMap: undefined,
    stopTracking: false,
  };
}

/**
 * Updates common labels tracking state.
 * Mutates the provided state object to optimize performance.
 * The term 'item' refers to a 'series' for 'TimeSeries' or 'value' for 'SingleValue', 'SingleValueTimeSeries'.
 */
export function updateCommonLabelsTracking(state: CommonLabelsTrackingState, item: LabeledItem): void {
  if (state.stopTracking) {
    return;
  }

  const formattedName = item.formattedName || item.name;
  const hasUnformattedLabels = item.labels && formattedName === item.name;

  if (hasUnformattedLabels && item.labels) {
    if (!state.commonLabelsMap) {
      // Initialize with first item's labels
      state.commonLabelsMap = new Map();
      for (const [key, val] of Object.entries(item.labels)) {
        state.commonLabelsMap.set(key, String(val));
      }
    } else {
      // Check which labels still have the same value across all items
      for (const [label, commonValue] of state.commonLabelsMap) {
        // If the label doesn't exist on this item, or has a different value, remove it
        if (!Object.hasOwn(item.labels, label) || String(item.labels[label]) !== commonValue) {
          state.commonLabelsMap.delete(label);
        }
      }
      // Stop tracking if no common labels remain
      if (state.commonLabelsMap.size === 0) {
        state.stopTracking = true;
      }
    }
  } else {
    // Stop tracking for mixed data sources
    state.stopTracking = true;
    state.commonLabelsMap = undefined;
  }
}

/**
 * Gets the final common labels result from the state.
 * Returns undefined if there are no common labels or tracking was stopped.
 */
export function getCommonLabelsResult(state: CommonLabelsTrackingState): Record<string, string> | undefined {
  if (state.stopTracking || !state.commonLabelsMap || state.commonLabelsMap.size === 0) {
    return undefined;
  }
  return Object.fromEntries(state.commonLabelsMap);
}

/**
 * Calculate statistics and common labels for query results.
 * Supports TimeSeries, SingleValue, and SingleValueTimeSeries data types.
 * Other data types are silently ignored and don't contribute to calculations.
 */
export function calculateStatsAndCommonLabels({
  queryResults,
  shouldCalcStats,
  shouldCalcCommonLabels,
}: {
  queryResults: QueryData[];
  shouldCalcStats: boolean;
  shouldCalcCommonLabels: boolean;
}) {
  if (!shouldCalcStats && !shouldCalcCommonLabels) {
    return { stats: undefined, commonLabels: undefined };
  }

  // Check if all queries are ready before calculations.
  // Summary of loading states:
  // - isLoading: true only on the initial fetch when no cached data exists
  // - isFetching: true whenever any query is fetching (including refetches)
  // - isFetchingMore: true when pagination is loading more data (progressive loading feature)
  //   - Left off here since we do want to incremental updates when calculating stats and common labels
  const allQueriesReady = queryResults.every((result) => result && !result.isLoading && !result.isFetching);
  if (!allQueriesReady) {
    return {
      stats: shouldCalcStats ? { totalSeries: 0, totalDatapoints: 0, isLoading: true } : undefined,
      commonLabels: undefined,
    };
  }

  let totalSeries = 0;
  let totalDatapoints = 0;
  let commonLabelsState = createCommonLabelsState();

  for (let i = 0; i < queryResults.length; i++) {
    const result = queryResults[i];
    // We know all queries are ready at this point (validated above)
    if (!result) continue;

    // Calculate stats and common labels for TimeSeries data
    if (result.type === 'TimeSeries' && result.data && isTimeSeriesData(result.data)) {
      // Count series for stats or common labels (needed for single series check)
      if (shouldCalcStats || shouldCalcCommonLabels) {
        totalSeries += result.data.series.length;
      }

      for (const series of result.data.series) {
        if (shouldCalcStats && series?.values) {
          totalDatapoints += series.values.length;
        }

        // Track common labels if requested
        if (shouldCalcCommonLabels && series) {
          updateCommonLabelsTracking(commonLabelsState, series);
        }
      }
    }

    // Calculate stats and common labels for SingleValue and SingleValueTimeSeries data
    if (
      (result.type === 'SingleValue' || result.type === 'SingleValueTimeSeries') &&
      result.data &&
      isSingleValueData(result.data)
    ) {
      // Count series for stats or common labels (needed for single series check)
      if (shouldCalcStats || shouldCalcCommonLabels) {
        totalSeries += result.data.values.length;
      }

      if (shouldCalcStats) {
        // Each SingleValue/SingleValueTimeSeries has exactly 1 datapoint (not counting the timeSeries sparkline array)
        totalDatapoints += result.data.values.length;
      }

      // Track common labels if requested
      if (shouldCalcCommonLabels) {
        for (const value of result.data.values) {
          updateCommonLabelsTracking(commonLabelsState, value);
        }
      }
    }
  }

  // Stop common labels tracking if we only have one series total
  if (shouldCalcCommonLabels && totalSeries <= 1) {
    commonLabelsState = { ...commonLabelsState, stopTracking: true };
  }

  return {
    stats: shouldCalcStats ? { totalSeries, totalDatapoints, isLoading: false } : undefined,
    commonLabels: shouldCalcCommonLabels ? getCommonLabelsResult(commonLabelsState) : undefined,
  };
}
